import { useState } from "react";
import {
  useDeleteAdminMutation,
  useFetchAdminsQuery,
  useToggleAdminIsActiveStatusMutation,
} from "../store";
import Pagination from "../components/common/Pagination";
import ToggleSwitch from "../components/common/ToggleSwitch";
import NavigationLinkBtn from "../components/common/NavigationLinkBtn";
import { Admin } from "../types";
import { showToast, ToastType } from "../utils/toast.utils";
import { FaExternalLinkAlt, FaRegTrashAlt } from "react-icons/fa";
import Modal from "../components/common/Modal";
import Button from "../components/common/Button";
import useDebounce from "../hooks/useDebounce";
import TextField from "../components/common/TextField";
import { Link } from "react-router-dom";

const Admins = () => {
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(20);
  const [name, setName] = useState<string>("");
  const debouncedName = useDebounce(name, 400);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState<Admin | null>(null);

  const [deleteAdmin] = useDeleteAdminMutation();
  const [toggleIsActive] = useToggleAdminIsActiveStatusMutation();

  const confirmDelete = (adminToDelete: Admin) => {
    setAdminToDelete(adminToDelete);
    setShowConfirmModal(true);
  };

  const handleDeleteAdmin = async () => {
    if (!adminToDelete) return;
    const result = await deleteAdmin(adminToDelete._id);
    if (!result.error) {
      showToast("Administrateur supprimé", ToastType.SUCCESS);
    }
    setShowConfirmModal(false);
    setAdminToDelete(null);
  };

  const { data: adminsInfo } = useFetchAdminsQuery({
    page,
    perPage,
    name: debouncedName,
  });

  return (
    <div className="p-6">
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Administrateurs
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
            <div className="w-full sm:w-72">
              <TextField
                id="name"
                value={name}
                onChange={(e) => setName((e.target as HTMLInputElement).value)}
                placeholder="Rechercher un administrateur..."
              />
            </div>
            <NavigationLinkBtn
              to="/admins/create"
              label="Créer un admin"
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nom Complet
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {adminsInfo?.data?.map((row) => (
                <tr
                  key={row._id}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {row.firstName} {row.lastName}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {row.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <div className="flex justify-center">
                      <ToggleSwitch
                        isOn={row.isActive}
                        onToggle={() => {
                          toggleIsActive({
                            adminId: row._id,
                            isActive: !row.isActive,
                          });
                        }}
                      />
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      <Link
                        to={row._id}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                        title="Détails"
                      >
                        <FaExternalLinkAlt className="w-3 h-3" />
                      </Link>
                      <button
                        onClick={() => confirmDelete(row)}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                        title="Supprimer"
                      >
                        <FaRegTrashAlt className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="border-t border-gray-200 p-4">
          <Pagination
            paginationInfo={{
              page,
              perPage,
              count: adminsInfo?.paginationInfo.count || 1,
            }}
            setPage={setPage}
            setPerPage={setPerPage}
          />
        </div>
      </div>

      {showConfirmModal && (
        <Modal
          onClose={() => setShowConfirmModal(false)}
          actionBar={
            <>
              <Button
                className="bg-gray-100 text-gray-700 hover:bg-gray-200 px-4 py-2 rounded mr-2"
                onClick={() => setShowConfirmModal(false)}
              >
                Annuler
              </Button>
              <Button
                className="bg-red-600 text-white hover:bg-red-700 px-4 py-2 rounded"
                onClick={handleDeleteAdmin}
              >
                Supprimer
              </Button>
            </>
          }
        >
          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Confirmer la suppression
            </h3>
            <p className="text-sm text-gray-500">
              Êtes-vous sûr de vouloir supprimer l&apos;administrateur{" "}
              <span className="font-semibold text-gray-900">
                {adminToDelete?.firstName} {adminToDelete?.lastName}
              </span>{" "}
              ? Cette action est irréversible.
            </p>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Admins;
